import Phaser from 'phaser';
import { CANVAS_W, CANVAS_H, PlayerId } from '../types';

/**
 * Always-on overlay scene that shows a per-player "CONTINUE? Y/N" prompt with a
 * 7-second countdown when a player runs out of lives. Each player has an
 * independent prompt rendered on their own half of the screen so the other
 * player's gameplay isn't interrupted.
 *
 * Activation/dismissal is done by GameScene calling `showFor(...)` /
 * `cancelFor(...)`. The overlay calls back into GameScene with `onContinue`
 * (Y pressed or button clicked) or `onSpectator` (N pressed, timer reached 0,
 * or scene shutdown).
 */
export interface ContinueRequest {
  playerId: PlayerId;
  playerName: string;
  accentColor: number; // 0xRRGGBB
  /** Phaser key code for "Yes / continue" — e.g. "Q" for P1, "ENTER" for P2. */
  yesKeyCode: string;
  /** Phaser key code for "No / spectator". */
  noKeyCode: string;
  /** Human-readable label like "Q" / "ENTER". */
  yesKeyLabel: string;
  noKeyLabel: string;
  onContinue: () => void;
  onSpectator: () => void;
}

const COUNTDOWN_MS = 7000;
const HALF_W = CANVAS_W / 2;

interface PromptState {
  request: ContinueRequest;
  deadlineAt: number;
  container: Phaser.GameObjects.Container;
  countdownText: Phaser.GameObjects.Text;
  yKey: Phaser.Input.Keyboard.Key;
  nKey: Phaser.Input.Keyboard.Key;
  resolved: boolean;
  // Prevent the same keypress that caused death from instantly answering "yes"
  respondAt: number;
}

export class ContinueOverlay extends Phaser.Scene {
  private prompts: Map<PlayerId, PromptState> = new Map();

  constructor() {
    super({ key: 'ContinueOverlay', active: false });
  }

  create() {
    // Clean any leftover prompts when the scene restarts
    this.events.on(Phaser.Scenes.Events.SHUTDOWN, () => {
      for (const state of this.prompts.values()) {
        if (!state.resolved) {
          state.resolved = true;
          state.request.onSpectator();
        }
        state.container.destroy();
      }
      this.prompts.clear();
    });
  }

  showFor(req: ContinueRequest) {
    // If a prompt is already up for this player, ignore the second request
    if (this.prompts.has(req.playerId)) return;

    const onLeftSide = req.playerId === 'p1';
    const panelX = onLeftSide ? 0 : HALF_W;
    const container = this.add.container(panelX, 0).setDepth(2000);

    // Semi-transparent dim only on this player's half
    const dim = this.add.graphics();
    dim.fillStyle(0x000000, 0.55).fillRect(0, 0, HALF_W, CANVAS_H);
    dim.lineStyle(3, req.accentColor, 0.9).strokeRect(2, 2, HALF_W - 4, CANVAS_H - 4);
    container.add(dim);

    const cx = HALF_W / 2;
    const cy = CANVAS_H / 2;

    const title = this.add.text(cx, cy - 110, 'CONTINUE?', {
      fontFamily: 'monospace',
      fontSize: '36px',
      color: '#' + req.accentColor.toString(16).padStart(6, '0'),
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 4,
    }).setOrigin(0.5);
    container.add(title);

    const subtitle = this.add.text(cx, cy - 68, `${req.playerName} a rămas fără vieți`, {
      fontFamily: 'monospace',
      fontSize: '16px',
      color: '#fff8d8',
    }).setOrigin(0.5);
    container.add(subtitle);

    const countdownText = this.add.text(cx, cy - 8, '7', {
      fontFamily: 'monospace',
      fontSize: '96px',
      color: '#ffd166',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 6,
    }).setOrigin(0.5);
    container.add(countdownText);

    const hintColor = req.playerId === 'p1' ? '(P1 tastatură stânga)' : '(P2 tastatură dreapta)';
    const hintLine = this.add.text(cx, cy + 70, hintColor, {
      fontFamily: 'monospace',
      fontSize: '12px',
      color: '#a0a0a0',
    }).setOrigin(0.5);
    container.add(hintLine);

    const yBtn = this.makeButton(cx - 70, cy + 110, `${req.yesKeyLabel} · DA`, 0x52ff7a, () => this.resolve(req.playerId, true));
    const nBtn = this.makeButton(cx + 70, cy + 110, `${req.noKeyLabel} · NU`, 0xff5577, () => this.resolve(req.playerId, false));
    container.add([yBtn.bg, yBtn.label, nBtn.bg, nBtn.label]);

    const helpText = this.add.text(cx, cy + 152,
      `${req.yesKeyLabel} = revino cu 3 vieți · ${req.noKeyLabel} / 0 = spectator`, {
      fontFamily: 'monospace',
      fontSize: '11px',
      color: '#cccccc',
    }).setOrigin(0.5);
    container.add(helpText);

    // Per-player keys: P1 uses their own plant/remote keys, P2 uses theirs.
    // This guarantees the two prompts can be answered independently — even if
    // both are open at the same time, one player's keypress only resolves
    // their own prompt.
    const kb = this.input.keyboard!;
    const yKey = kb.addKey(req.yesKeyCode);
    const nKey = kb.addKey(req.noKeyCode);

    const state: PromptState = {
      request: req,
      deadlineAt: this.time.now + COUNTDOWN_MS,
      container,
      countdownText,
      yKey,
      nKey,
      resolved: false,
      respondAt: this.time.now + 350, // 350ms grace — ignore same-frame Q that caused death
    };
    this.prompts.set(req.playerId, state);

    // Subtle pulse to draw the eye
    this.tweens.add({
      targets: countdownText,
      scale: { from: 1, to: 1.15 },
      yoyo: true,
      repeat: -1,
      duration: 500,
      ease: 'Sine.easeInOut',
    });
  }

  /** External cancel (e.g. level change). Resolves as spectator without callback. */
  cancelFor(playerId: PlayerId, fireSpectatorCallback = false) {
    const state = this.prompts.get(playerId);
    if (!state) return;
    state.resolved = true;
    state.container.destroy();
    this.prompts.delete(playerId);
    if (fireSpectatorCallback) state.request.onSpectator();
  }

  update() {
    for (const [pid, state] of this.prompts) {
      if (state.resolved) continue;
      const remaining = Math.max(0, state.deadlineAt - this.time.now);
      const seconds = Math.ceil(remaining / 1000);
      state.countdownText.setText(String(seconds));
      // Color slides toward red as time runs out
      if (seconds <= 2) state.countdownText.setColor('#ff5577');
      else if (seconds <= 4) state.countdownText.setColor('#ffaa44');
      else state.countdownText.setColor('#ffd166');

      if (this.time.now >= state.respondAt) {
        if (Phaser.Input.Keyboard.JustDown(state.yKey)) {
          this.resolve(pid, true);
          continue;
        }
        if (Phaser.Input.Keyboard.JustDown(state.nKey)) {
          this.resolve(pid, false);
          continue;
        }
      }
      if (remaining <= 0) {
        this.resolve(pid, false);
      }
    }
  }

  private resolve(playerId: PlayerId, didContinue: boolean) {
    const state = this.prompts.get(playerId);
    if (!state || state.resolved) return;
    state.resolved = true;
    state.container.destroy();
    this.prompts.delete(playerId);
    if (didContinue) state.request.onContinue();
    else state.request.onSpectator();
  }

  private makeButton(x: number, y: number, label: string, color: number, onClick: () => void) {
    const w = 110;
    const h = 36;
    const bg = this.add.graphics();
    bg.fillStyle(color, 0.85).fillRoundedRect(x - w / 2, y - h / 2, w, h, 6);
    bg.lineStyle(2, 0x000000, 1).strokeRoundedRect(x - w / 2, y - h / 2, w, h, 6);
    bg.setInteractive(new Phaser.Geom.Rectangle(x - w / 2, y - h / 2, w, h), Phaser.Geom.Rectangle.Contains);
    bg.on('pointerdown', onClick);
    const text = this.add.text(x, y, label, {
      fontFamily: 'monospace',
      fontSize: '16px',
      color: '#131120',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    return { bg, label: text };
  }
}
